<template>
    <tr class="h-12 whitespace-nowrap bg-gray-150 flex items-center border-b border-t-2 border-gray-300 font-medium  text-gray-800 tracking-wider">
        <td :colspan="columnSpan" class="w-full h-full grid grid-cols-3 justify-start items-center">
            <div v-if="!isLoading" class="pl-3 whitespace-nowrap w-full flex space-x-2">

                <div v-if="isPastEnd" class="whitespace-nowrap italic text-gray-500">
                    Past the last page &mdash; use &lt; to get back to the results
                </div>
                <div v-else-if="viewingFromNbr==viewingToNbr" class="whitespace-nowrap">
                    Results: {{ displayedViewFrom }} of {{ displayedCount }}
                </div>
                <div v-else class="whitespace-nowrap">
                    Results: {{ displayedViewFrom }}-{{ displayedViewTo }} of {{ displayedCount }}
                </div>
                <label
                    class="flex items-center space-x-1 whitespace-nowrap font-normal text-[0.65rem] leading-tight text-gray-600 hover:cursor-pointer"
                    title="Show the exact result count instead of capping at 1,000."
                >
                    <input
                        type="checkbox"
                        class="hover:cursor-pointer"
                        :checked="exactCount"
                        @change="setExactCount($event.target.checked)"
                    />
                    <span>Show full count</span>
                </label>
                <div  class="whitespace-nowrap" v-if="selectedResults.length > 0">
                    ( {{ selectedResults.length }} Selected {{ offPageSelectedCount }} )
                </div>
            </div>
            <div v-else class="pl-3 whitespace-nowrap w-full flex space-x-2 animate-pulse">
                <div class="h-4 w-16 rounded bg-gray-300"></div>
                <div class="h-4 w-32 rounded bg-gray-300"></div>
            </div>
            <div v-if="!isLoading" class="flex flex-row items-end justify-center px-2 w-full">
                <!-- <div v-if="isLoading" role="status" class="">
                    <svg
                    aria-hidden="true"
                    class="w-5 h-5 animate-spin"
                    viewBox="0 0 100 101"
                    fill="none"
                    xmlns="http://www.w3.org/2000/svg"
                    >
                    <path
                        d="M100 50.5908C100 78.2051 77.6142 100.591 50 100.591C22.3858 100.591 0 78.2051 0 50.5908C0 22.9766 22.3858 0.59082 50 0.59082C77.6142 0.59082 100 22.9766 100 50.5908ZM9.08144 50.5908C9.08144 73.1895 27.4013 91.5094 50 91.5094C72.5987 91.5094 90.9186 73.1895 90.9186 50.5908C90.9186 27.9921 72.5987 9.67226 50 9.67226C27.4013 9.67226 9.08144 27.9921 9.08144 50.5908Z"
                        fill="white"
                    />
                    <path
                        d="M93.9676 39.0409C96.393 38.4038 97.8624 35.9116 97.0079 33.5539C95.2932 28.8227 92.871 24.3692 89.8167 20.348C85.8452 15.1192 80.8826 10.7238 75.2124 7.41289C69.5422 4.10194 63.2754 1.94025 56.7698 1.05124C51.7666 0.367541 46.6976 0.446843 41.7345 1.27873C39.2613 1.69328 37.813 4.19778 38.4501 6.62326C39.0873 9.04874 41.5694 10.4717 44.0505 10.1071C47.8511 9.54855 51.7191 9.52689 55.5402 10.0491C60.8642 10.7766 65.9928 12.5457 70.6331 15.2552C75.2735 17.9648 79.3347 21.5619 82.5849 25.841C84.9175 28.9121 86.7997 32.2913 88.1811 35.8758C89.083 38.2158 91.5421 39.6781 93.9676 39.0409Z"
                        fill="#2E9CCA"
                    />
                    </svg>
                </div> -->
                <PaginationArrows
                    @get-next="getNext"
                    @go-to-first="goToFirst"
                    @get-prev="getPrev"
                    @go-to-last="goToLast"
                    @jump-to-page="jumpToPage"
                    :has-next="hasNext"
                    :has-prev="hasPrev"
                    :count-is-capped="countIsCapped"
                    :is-past-end="isPastEnd"
                    :page="page"
                    :numPages="numPages"
                ></PaginationArrows>
            </div>
            <div v-else class="flex pl-2 flex-row items-end justify-center px-2 w-full space-x-2  animate-pulse">
                <div class="h-4 w-16 rounded bg-gray-300"></div>
                <div class="h-4 w-28 rounded bg-gray-300"></div>
                <div class="h-4 w-16 rounded bg-gray-300"></div>
            </div>
            <div class="h-full"></div>
        </td>
    </tr>
</template>

<script setup>
import { ref, defineProps, defineEmits, computed } from 'vue';
import PaginationArrows from '../base-components/PaginationArrows.vue';

const props = defineProps([
    'isLoading', 
    'viewingFromNbr', 
    'viewingToNbr', 
    'count',
    'numPages', 
    'selectedResults',
    'page',
    'columnSpan',
    'results',
    'hasNext',
    'hasPrev',
    'countIsCapped',
    'isPastEnd',
    'exactCount'
])

const emit = defineEmits([
    'get-next',
    'go-to-first',
    'get-prev',
    'go-to-last',
    'jump-to-page',
    'set-page-size-filter',
    'set-exact-count'
])

function getNext() {
    emit("get-next");
  }
  
  function goToFirst() {
    emit("go-to-first");
  }
  
  function getPrev() {
    emit("get-prev");
  }
  
  function goToLast() {
    emit("go-to-last");
  }
  
  function jumpToPage(targetPage) {
    emit("jump-to-page", targetPage);
  }

  function setExactCount(value) {
    emit("set-exact-count", value);
  }

  const resultIds = computed(() => {
    return props.results.map((result) => {
        return result.id
    })
  })

  const offPageSelectedResults = computed(() => {
    return props.selectedResults.filter((result) => {
        return !resultIds.value.includes(result.id)
    })
  })

const offPageSelectedCount = computed(() => {
    if(offPageSelectedResults.value.length > 0){
        return `| ${offPageSelectedResults.value.length} Off Page`
    } else {
        return ""
    }
})

const displayedViewFrom  = computed(() => {
    return props.viewingFromNbr ? props.viewingFromNbr.toLocaleString() : 0
})

 const displayedViewTo = computed(() => {
    return props.viewingToNbr ? props.viewingToNbr.toLocaleString() : 0
})

const displayedCount = computed(() => {
    if (!props.count) return 0
    // A capped count is a floor, not a total — the arrows already read "20+", so
    // the total must not claim to be exact either.
    const formatted = typeof props.count === 'number' ? props.count.toLocaleString() : props.count
    return props.countIsCapped ? `${formatted}+` : formatted
})
  
</script>